import React from "react";
import { Check, ExternalLink, Loader2, UserPlus } from "lucide-react";
import { Modal, ModalContent, ModalFooter } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { EntityLink } from "../../components/ui/EntityLink";

// Shown only when the catalogue already holds something all but identically named. Nothing dedups
// an accepted artist automatically anymore — the research agent is not asked for a MusicBrainz
// identity — so this is the moment a second copy of an act gets prevented rather than discovered
// in duplicate review afterwards. No matches means no modal: it must not become a click to dismiss.
const ArtistMatchModal = ({ open, onOpenChange, proposedName, matches, total, busy, onAttach, onCreate }) => (
  <Modal open={open} onOpenChange={busy ? undefined : onOpenChange}>
    <ModalContent
      className="max-w-2xl"
      closeDisabled={busy}
      title={`“${proposedName}” may already be in Applauss`}
      description={
        matches.length === 1 && total <= 1
          ? "One artist has almost exactly this name. Attach the event to it, or create a new one."
          : `${total > matches.length ? `${total} artists have almost exactly this name — showing the closest ${matches.length}` : `${matches.length} artists have almost exactly this name`}. Attach the event to one, or create a new one.`
      }
    >
      <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
        <ul className="space-y-3">
          {matches.map((match) => {
            const displayName = match.preferred_name || match.name;
            const socials = Object.entries(match.links || {});
            return (
              <li key={match.id} className="rounded-lg border p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{displayName}</span>
                      {match.preferred_name && match.preferred_name !== match.name && (
                        <span className="text-xs text-muted-foreground">provider: {match.name}</span>
                      )}
                      {/* The score is what put this row in front of the admin, so it is shown
                          rather than hidden — an exact match and a 0.96 deserve different care. */}
                      <Badge variant="secondary">{Math.round(match.similarity * 100)}% name match</Badge>
                      {match.has_musicbrainz && <Badge variant="outline">MusicBrainz</Badge>}
                      {match.country && <Badge variant="outline">{match.country}</Badge>}
                    </div>

                    {socials.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                        {socials.map(([platform, url]) => (
                          <a
                            key={platform}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            {platform}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button size="sm" disabled={busy} onClick={() => onAttach(match)}>
                    {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />}
                    This one
                  </Button>
                </div>

                {/* The booking history is the evidence a name cannot give. Two records for the
                    same act usually show it: the same venues, the same kind of bill. */}
                <div className="mt-2 border-t pt-2">
                  <p className="text-xs text-muted-foreground">
                    {match.event_count === 0
                      ? "No events reference this artist"
                      : `${match.event_count} event${match.event_count === 1 ? "" : "s"}${
                          match.events?.length < match.event_count ? ` — showing ${match.events.length}` : ""
                        }`}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {(match.events || []).map((event) => (
                      <li key={event.id} className="text-xs">
                        <EntityLink type="event" id={event.id} newTab className="text-primary">
                          {event.title || event.id}
                        </EntityLink>
                        {event.start_date && <span className="text-muted-foreground"> · {event.start_date}</span>}
                        {event.is_past && <span className="text-muted-foreground"> · past</span>}
                        {event.is_cancelled && <span className="text-muted-foreground"> · cancelled</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <ModalFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
        <p className="text-xs text-muted-foreground sm:mr-auto">
          Not the same act? Creating a new artist is still the right answer — a tribute band is not
          the band it covers.
        </p>
        <Button variant="outline" disabled={busy} onClick={onCreate}>
          <UserPlus className="mr-1 h-3 w-3" />
          Create “{proposedName}”
        </Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

export default ArtistMatchModal;
